import React, { useEffect, useState } from 'react'
import md5 from 'md5'
import { InputCP } from 'main/common/components/form-fields/input/InputCP'
import { LockIconCP } from 'main/common/components/icons/LockIconCP'
import { MailIconCP } from 'main/common/components/icons/MailIconCP'
import { HttpStatusEnum } from 'main/common/enums/HttpStatusEnum'
import { useFormStateManager } from 'main/common/form-state-manager/UseFormStateManager'
import { useRequest } from 'main/common/request-manager/use-request/UseRequest'
import { AuthRequests } from 'main/modules/auth/services/AuthRequests'
import { AuthFormFooterCP } from 'main/modules/auth/components/auth-form-footer/AuthFormFooterCP'
import { UserLoginFormValidator } from 'main/modules/auth/components/user-login-form/inner/UserLoginFormValidator'
import { RequestUtils } from 'main/common/request-manager/RequestUtils'
import { SchemaIconCP } from 'main/common/components/icons/SchemaIconCP'
import { AppStateUtils } from 'main/common/utils/AppStateUtils'
import { IAuthResponseDTO } from 'main/modules/auth/services/dtos/response/IAuthResponseDTO'
import { ISchemaResponseDTO } from 'main/modules/app/services/dtos/response/ISchemaResponseDTO'
import { IUserLoginRequestDTO } from 'main/modules/auth/services/dtos/request/IUserLoginRequestDTO'
import { AuthActions } from 'main/modules/auth/AuthActions'
import { SysAdminRequests } from 'main/modules/app/services/SysAdminRequests'
import styled from 'styled-components'

interface ILoginFormICPProps {
    onRecoverPassword: () => void
    onLoginSuccess: () => void
}

/**
 * COMPONENTE
 * Formulario de login de usuario do sistema.
 */
export function LoginFormCP(props: ILoginFormICPProps): JSX.Element {

    const [formValidator] = useState<UserLoginFormValidator>(new UserLoginFormValidator({ schema: AppStateUtils.getDomainSlug() }))

    const loginRequest = useRequest<IAuthResponseDTO>()
    const domainSchemaRequest = useRequest<ISchemaResponseDTO>()

    const formStateManager = useFormStateManager(formValidator)

    useEffect(onLoginRequestChange, [loginRequest.isAwaiting])
    useEffect(onSchemaRequestChange, [domainSchemaRequest.isAwaiting])

    async function onFormSubmit(): Promise<void> {

        formStateManager.setConsiderAllErrors(true)
        if (!await formStateManager.validate())
            return

        const formValues = formStateManager.getFormValues()!

        const loginDto: IUserLoginRequestDTO = {
            email: formValues.email,
            password: md5(formValues.password)
        }

        loginRequest.runRequest(AuthRequests.login(formValues.schema, loginDto))
    }

    function onLoginRequestChange(): void {

        if (loginRequest.isAwaiting || !loginRequest.wasTried)
            return

        const isSuccess = (loginRequest.isSuccess && !!loginRequest.responseData)
        if (!isSuccess)
            return handleLoginFailure()

        const schemaOnForm = formStateManager.getFieldValue('schema')
        const resDto = loginRequest.responseData as IAuthResponseDTO

        if (AppStateUtils.getDomainSlug() === schemaOnForm)
            return onLoginSuccess(resDto)

        domainSchemaRequest.runRequest(SysAdminRequests.getSchemaDataConfig(schemaOnForm, resDto.token))
    }

    /** Retorno de dados do schema, chamado depois do login. */
    function onSchemaRequestChange(): void {
        if (RequestUtils.isValidRequestReturn({
            request: domainSchemaRequest,
            errorMsg: 'Falha ao capturar dados do dominio',
            failureLogMsg: 'FALHA - LoginFormCP.onSchemaRequestChange: ',
            mustReport403: true,
        })) {
            AuthActions.setDomain(domainSchemaRequest.responseData!)
            onLoginSuccess(loginRequest.responseData!)
        }
    }

    /** Se deu certo o login. */
    function onLoginSuccess(_loggedUser: IAuthResponseDTO): void {
        AuthActions.login(_loggedUser.user, _loggedUser.token)
        props.onLoginSuccess()
    }

    /** Trata casos de erro no login. */
    function handleLoginFailure(): void {

        let errorNotification = 'Falha ao tentar realizar login'

        if (loginRequest.responseStatus === HttpStatusEnum.FORBIDDEN)
            errorNotification = 'Domínio inativo ou não reconhecido'

        else if ([HttpStatusEnum.UNAUTHORIZED, HttpStatusEnum.NOT_FOUND].includes(loginRequest.responseStatus!))
            errorNotification = 'e-mail ou senha inválidos'
        else
            console.error('FALHA - UserLoginFormCP.handleLoginFailure: ', 'Falha inesperada', loginRequest.responseData, loginRequest.error)

        RequestUtils.showDefaultErrorNotification(loginRequest.error, errorNotification)
    }

    return (
        <FormContainerSCP>
            <InputCP
                label={'Dominio'}
                fieldName={'schema'}
                formStateManager={formStateManager}
                required={true}
                onFormSubmit={onFormSubmit}
                icon={<SchemaIconCP/>}
            />

            <InputCP
                label={'E-mail'}
                fieldName={'email'}
                formStateManager={formStateManager}
                required={true}
                onFormSubmit={onFormSubmit}
                icon={<MailIconCP/>}
            />

            <InputCP
                label={'Senha'}
                type={'password'}
                fieldName={'password'}
                formStateManager={formStateManager}
                required={true}
                onFormSubmit={onFormSubmit}
                icon={<LockIconCP/>}
            />

            <AuthFormFooterCP
                link={{
                    text: 'Esqueci minha senha',
                    onClick: props.onRecoverPassword,
                }}
                button={{
                    text: 'Entrar',
                    onClick: onFormSubmit,
                }}
                awaiting={loginRequest.isAwaiting || domainSchemaRequest.isAwaiting}
            />
        </FormContainerSCP>
    )
}

const FormContainerSCP = styled.div`
  width: 100%;
  max-width: 365px;
  height: 100%;
  margin-top: 30px;
`
